<script setup lang="ts">
  import type { ProductVariantResource } from '@storefront/resources';

  interface Props {
    variant: ProductVariantResource;
  }

  defineProps<Props>();
</script>
<template>
  <div class="product-safety">
    <div v-if="variant.productSafetyNotes.length > 0" class="notes">
      <ul>
        <li v-for="note in variant.productSafetyNotes" v-html="note.description" />
      </ul>
    </div>
    <div class="entities">
      <div v-if="variant.productManufacturer" class="entity">
        <p class="label">{{ $t('SHOP.COMMON.LABEL.MANUFACTURER') }}</p>
        <h3>{{ variant.productManufacturer.name }}</h3>
        <SfAddressInline :segments="[variant.productManufacturer.firstLine, variant.productManufacturer.secondLine, variant.productManufacturer.thirdLine, variant.productManufacturer.countryCode]" />
        <p v-if="variant.productManufacturer.email">
          <a :href="`mailto:${variant.productManufacturer.email}`" target="_blank" v-text="variant.productManufacturer.email" />
        </p>
        <p v-if="variant.productManufacturer.phone">
          <a :href="`tel:${variant.productManufacturer.phone}`" target="_blank" v-text="variant.productManufacturer.phone" />
        </p>
      </div>
      <div v-if="variant.productResponsibleEntity" class="entity">
        <p class="label">{{ $t('SHOP.COMMON.LABEL.RESPONSIBLE_ENTITY') }}</p>
        <h3>{{ variant.productResponsibleEntity.name }}</h3>
        <SfAddressInline :segments="[variant.productResponsibleEntity.firstLine, variant.productResponsibleEntity.secondLine, variant.productResponsibleEntity.thirdLine, variant.productResponsibleEntity.countryCode]" />
        <p v-if="variant.productResponsibleEntity.email">
          <a :href="`mailto:${variant.productResponsibleEntity.email}`" target="_blank" v-text="variant.productResponsibleEntity.email" />
        </p>
        <p v-if="variant.productResponsibleEntity.phone">
          <a :href="`tel:${variant.productResponsibleEntity.phone}`" target="_blank" v-text="variant.productResponsibleEntity.phone" />
        </p>
      </div>
      <div v-for="importer in variant.productImporters" class="entity">
        <p class="label">{{ $t('SHOP.COMMON.LABEL.IMPORTER') }}</p>
        <h3>{{ importer.name }}</h3>
        <SfAddressInline :segments="[importer.firstLine, importer.secondLine, importer.thirdLine, importer.countryCode]" />
        <p v-if="importer.email">
          <a :href="`mailto:${importer.email}`" target="_blank" v-text="importer.email" />
        </p>
        <p v-if="importer.phone">
          <a :href="`tel:${importer.phone}`" target="_blank" v-text="importer.phone" />
        </p>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .product-safety {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .notes {
      ul {
        margin: 0 0 0 1.2rem;
        padding: 0;
      }
    }

    .entities {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1rem;

      .entity {
        font-size: 0.875rem;

        .label {
          font-weight: var(--font-weight-semi);
        }

        h3 {
          font-weight: var(--font-weight-regular);
          font-size: 0.875rem;
        }

        address {
          font-style: normal;
        }
      }

      a {
        text-decoration: underline;
        text-decoration-color: transparent;
        transition: text-decoration-color 0.2s ease-out;
      }

      @media(hover: hover) {
        a:hover {
          text-decoration-color: var(--primary-color);
        }
      }
    }

    @include media.query(lg) {
      padding: 0 0.625rem;
    }
  }
</style>
